import {
  Box,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Grid,
} from "@mui/material";
import React from "react";

import { language } from "../../../../language";
import { getUnitPropertie } from "../../../smart/camunda/forms/functions";

function escapeAttribute(value) {
  return String(value).replace(/"/g, "&quot;");
}

function normalizeStyleValue(value) {
  return String(value || "").trim();
}

function getTypographyStyle(props) {
  const declarations = [];
  const color = normalizeStyleValue(props.color);
  const fontFamily = normalizeStyleValue(props.fontFamily);
  const fontSize = normalizeStyleValue(props.fontSize);
  const opacity =
    props.opacity === 0 || props.opacity
      ? normalizeStyleValue(props.opacity)
      : "";

  if (fontFamily) declarations.push(`font-family: ${fontFamily}`);
  if (color && !["primary", "secondary", "error"].includes(color)) {
    declarations.push(`color: ${color}`);
  }
  if (fontSize) declarations.push(`font-size: ${fontSize}`);
  if (opacity) declarations.push(`opacity: ${opacity}`);
  if (props.typographyWeight === "bold") {
    declarations.push("font-weight: bold");
  }
  if (props.typographyWeight === "italic") {
    declarations.push("font-style: italic");
  }
  if (props.typographyWeight === "italicBold") {
    declarations.push("font-style: italic");
    declarations.push("font-weight: bold");
  }

  return declarations.join("; ");
}

function getTypographyTag(variant) {
  return ["h1", "h2", "h3", "h4", "h5", "h6"].includes(variant)
    ? variant
    : "span";
}

function getPropertiesWithValue(state, name, value) {
  const customProps = state.properties ? Array.from(state.properties) : [];
  const model = customProps.find((item) => item.id === name);

  if (model) {
    model.value = value;
  } else {
    customProps.push({ id: name, value, text: name, type: "text" });
  }

  return customProps;
}

function getElementReplacementCandidate(node, selectedText) {
  const element = node.parentElement;

  if (
    element &&
    /^(span|h[1-6])$/i.test(element.tagName) &&
    element.textContent === selectedText
  ) {
    return element;
  }

  return node;
}

function replaceOccurrenceInHtml(
  html,
  selectedText,
  occurrenceIndex,
  replacement,
) {
  if (typeof window === "undefined" || !window.DOMParser) {
    return String(html).replace(selectedText, replacement);
  }

  const doc = new DOMParser().parseFromString(
    `<body>${String(html)}</body>`,
    "text/html",
  );
  const walker = doc.createTreeWalker(doc.body, NodeFilter.SHOW_TEXT);
  let currentNode = walker.nextNode();
  let currentOccurrence = 0;

  while (currentNode) {
    const text = currentNode.textContent || "";
    let searchFrom = 0;
    let foundIndex = text.indexOf(selectedText, searchFrom);

    while (foundIndex !== -1) {
      if (currentOccurrence === occurrenceIndex) {
        const targetNode = getElementReplacementCandidate(
          currentNode,
          selectedText,
        );
        const template = doc.createElement("template");

        if (targetNode === currentNode) {
          template.innerHTML = `${text.slice(
            0,
            foundIndex,
          )}${replacement}${text.slice(foundIndex + selectedText.length)}`;
        } else {
          template.innerHTML = replacement;
        }

        targetNode.replaceWith(template.content);
        return doc.body.innerHTML;
      }

      currentOccurrence += 1;
      searchFrom = foundIndex + selectedText.length;
      foundIndex = text.indexOf(selectedText, searchFrom);
    }

    currentNode = walker.nextNode();
  }

  return html;
}

function applyTypographyStyleToSelection(state, selection, nextValues) {
  const label = getUnitPropertie(state, "label") || "";
  const selectedText = String(selection?.text || selection || "");
  const occurrenceIndex = Number(selection?.occurrenceIndex || 0);

  if (!label || !selectedText) {
    return label;
  }

  const style = getTypographyStyle(nextValues);
  const tag = getTypographyTag(nextValues.variant);
  const styleAttribute = style ? ` style="${escapeAttribute(style)}"` : "";
  const replacement = `<${tag}${styleAttribute}>${selectedText}</${tag}>`;

  return replaceOccurrenceInHtml(
    label,
    selectedText,
    occurrenceIndex,
    replacement,
  );
}

export default class Typograph {
  static DisplayTab(props) {
    const { onChange, state, onPropertieChange } = props;
    const label = getUnitPropertie(state, "label");

    return (
      <Grid container spacing={2}>
        <Grid item size={12}>
          <TextField
            onChange={(event) => onChange(event)}
            name="id"
            variant="outlined"
            id="id"
            label="Id"
            value={state.id || ""}
            fullWidth
            helperText={language.toolTipId}
          />
        </Grid>

        <Grid item size={12}>
          <TextField
            onChange={(event) => onPropertieChange(event)}
            name="label"
            variant="outlined"
            id="label"
            label="Texto"
            value={label || ""}
            multiline
            rows="10"
            fullWidth
            helperText={language.toolTipLabel}
          />
        </Grid>
      </Grid>
    );
  }

  static StylesTab(props) {
    const { onPropertieChange, state, setObjeto, previewSelection } = props;

    const color = getUnitPropertie(state, "color");
    const fontFamily = getUnitPropertie(state, "fontFamily");
    const typographyWeight = getUnitPropertie(state, "typographyWeight");
    const fontSize = getUnitPropertie(state, "fontSize");
    const opacity = getUnitPropertie(state, "opacity");
    const propertieVariant = getUnitPropertie(state, "variant");

    const handleHexColorChange = (event) => {
      const { value } = event.target;
      if (/^#[0-9A-Fa-f]{0,6}$/.test(value) || value === "") {
        if (value === "" || /^#[0-9A-Fa-f]{6}$/.test(value)) {
          handleStyleChange(event);
        } else {
          onPropertieChange(event);
        }
      }
    };

    const handleStyleChange = (event) => {
      const { name, value } = event.target;

      if (!setObjeto || !previewSelection?.text) {
        onPropertieChange(event);
        return;
      }

      const nextValues = {
        color,
        fontFamily,
        typographyWeight,
        fontSize,
        opacity,
        variant: propertieVariant,
        [name]: value,
      };
      const customProps = getPropertiesWithValue(state, name, value);
      const nextLabel = applyTypographyStyleToSelection(
        { ...state, properties: customProps },
        previewSelection,
        nextValues,
      );
      const labelProps = getPropertiesWithValue(
        { ...state, properties: customProps },
        "label",
        nextLabel,
      );

      setObjeto({
        ...state,
        properties: labelProps,
      });
    };

    return (
      <Grid container spacing={2}>
        <Grid item size={12}>
          <InputLabel>Fonte</InputLabel>
          <Select
            name="fontFamily"
            onChange={(event) => handleStyleChange(event)}
            value={fontFamily || ""}
            fullWidth
            variant="outlined"
          >
            <MenuItem value="Arial">Arial</MenuItem>
            <MenuItem value="Inter">Inter</MenuItem>
            <MenuItem value="Times New Roman">Times New Roman</MenuItem>
            <MenuItem value="Roboto">Roboto</MenuItem>
          </Select>
        </Grid>

        <Grid item size={12}>
          <InputLabel>Peso</InputLabel>
          <Select
            name="typographyWeight"
            onChange={(event) => handleStyleChange(event)}
            value={typographyWeight || ""}
            fullWidth
            variant="outlined"
          >
            <MenuItem value="">Regular</MenuItem>
            <MenuItem value="bold">Bold</MenuItem>
            <MenuItem value="italic">Itálico</MenuItem>
            <MenuItem value="italicBold">Itálico Bold</MenuItem>
          </Select>
        </Grid>

        <Grid item size={12}>
          <Box sx={{ display: "flex", alignItems: "stretch", gap: 1 }}>
            <TextField
              onChange={handleHexColorChange}
              name="color"
              variant="outlined"
              label={language.propertieColor}
              value={color || ""}
              placeholder="#1960ca"
              inputProps={{ maxLength: 7 }}
              fullWidth
            />
            <Box
              component="label"
              sx={{
                backgroundColor: /^#[0-9A-Fa-f]{6}$/.test(color || "")
                  ? color
                  : "#000000",
                boxSizing: "border-box",
                height: 56,
                minHeight: 56,
                width: 64,
                minWidth: 64,
                border: "1px solid rgba(0, 0, 0, 0.23)",
                borderRadius: 1,
                boxShadow: "inset 0 0 0 4px #fff",
                cursor: "pointer",
                overflow: "hidden",
                position: "relative",
                "&:hover": {
                  borderColor: "rgba(0, 0, 0, 0.87)",
                },
              }}
            >
              <Box
                component="input"
                type="color"
                name="color"
                value={
                  /^#[0-9A-Fa-f]{6}$/.test(color || "") ? color : "#000000"
                }
                onChange={(event) => handleStyleChange(event)}
                sx={{
                  border: 0,
                  cursor: "pointer",
                  height: "100%",
                  left: 0,
                  opacity: 0,
                  padding: 0,
                  position: "absolute",
                  top: 0,
                  width: "100%",
                }}
              />
            </Box>
          </Box>
        </Grid>

        <Grid item size={12}>
          <TextField
            onChange={(event) => handleStyleChange(event)}
            name="opacity"
            variant="outlined"
            label="Alpha"
            type="number"
            value={opacity ?? ""}
            inputProps={{ min: 0, max: 1, step: 0.01 }}
            fullWidth
          />
        </Grid>

        <Grid item size={12}>
          <InputLabel>Variante</InputLabel>
          <Select
            name="variant"
            onChange={(event) => handleStyleChange(event)}
            value={propertieVariant || ""}
            fullWidth
            variant="outlined"
          >
            <MenuItem value="">Default</MenuItem>
            <MenuItem value="h1">h1</MenuItem>
            <MenuItem value="h2">h2</MenuItem>
            <MenuItem value="h3">h3</MenuItem>
            <MenuItem value="h4">h4</MenuItem>
            <MenuItem value="h5">h5</MenuItem>
            <MenuItem value="h6">h6</MenuItem>
            <MenuItem value="subtitle1">subtitle1</MenuItem>
            <MenuItem value="subtitle2">subtitle2</MenuItem>
            <MenuItem value="body1">body1</MenuItem>
            <MenuItem value="body2">body2</MenuItem>
            <MenuItem value="caption">caption</MenuItem>
            <MenuItem value="button">button</MenuItem>
            <MenuItem value="srOnly">srOnly</MenuItem>
            <MenuItem value="inherit">inherit</MenuItem>
          </Select>
        </Grid>

        <Grid item size={12}>
          <InputLabel>Tamanho</InputLabel>
          <Select
            name="fontSize"
            onChange={(event) => handleStyleChange(event)}
            value={fontSize || ""}
            fullWidth
            variant="outlined"
          >
            {[10, 11, 12, 13, 14, 15, 16, 20].map((size) => (
              <MenuItem key={size} value={`${size}px`}>
                {size}px
              </MenuItem>
            ))}
          </Select>
        </Grid>
      </Grid>
    );
  }
}
